import type {
  ColumnRowGroupChangedEvent,
  ExpandOrCollapseAllEvent,
  RowGroupOpenedEvent,
  StateUpdatedEvent,
} from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useBlotterTableStorage, type RowGroupExpansionState } from './BlotterTableStorageContext';
import type { DefaultedUsePersistedBlotterTableOptions } from './usePersistedBlotterTableUtils';
import { getFirstRowGroupNode, safeGridApi } from './utils';

const ROW_GROUP_EXPANSION_EXCEPTIONS_MAX = 200;

export interface UsePersistedRowGroupExpansionParams {
  blotterID: string | null;
  options: Pick<DefaultedUsePersistedBlotterTableOptions, 'persistGridState' | 'persistRowGroupExpansion'>;
}

export interface UsePersistedRowGroupExpansionOutput {
  /** Event handlers that need to be registered with AgGrid (done by useBlotterTable) for this hook's persistence model to work */
  eventHandlers: {
    onExpandOrCollapseAll: (event: ExpandOrCollapseAllEvent) => void;
    onColumnRowGroupChanged: (event: ColumnRowGroupChangedEvent) => void;
    onRowGroupOpened: (event: RowGroupOpenedEvent) => void;
  };

  /** To be used when AgGrid's gridInitialized event comes through to check out and maybe mutate its rowGroupExpansion state */
  onStateUpdatedInterceptor: (event: StateUpdatedEvent) => StateUpdatedEvent;
}

/**
 * Persist row group expansion in a compact form:
 * - global expansion rule (`all-expanded` / `all-collapsed`)
 * - rowGroup node id exceptions
 */
export function usePersistedRowGroupExpansion({
  blotterID,
  options,
}: UsePersistedRowGroupExpansionParams): UsePersistedRowGroupExpansionOutput {
  const { persistGridState, persistRowGroupExpansion } = options;
  const { getState, setExtraGridState } = useBlotterTableStorage();

  const persistRowGroupExpansionState = useCallback(
    (rowGroupExpansion: RowGroupExpansionState | undefined) => {
      if (!blotterID || !persistGridState || !persistRowGroupExpansion) {
        return;
      }

      const currentState = getState(blotterID);
      setExtraGridState(blotterID, {
        ...currentState?.extraGridState,
        rowGroupExpansion,
      });
    },
    [blotterID, getState, persistGridState, persistRowGroupExpansion, setExtraGridState]
  );

  const onExpandOrCollapseAll = useCallback(
    (event: ExpandOrCollapseAllEvent) => {
      const api = safeGridApi(event.api);
      if (!api) {
        return;
      }

      const someGroupNode = getFirstRowGroupNode(api);
      if (!someGroupNode) {
        return;
      }

      const expansionState: RowGroupExpansionState['expansionState'] = someGroupNode.expanded
        ? 'all-expanded'
        : 'all-collapsed';
      persistRowGroupExpansionState({
        expansionState,
        expansionExceptions: [],
      });
    },
    [persistRowGroupExpansionState]
  );

  const onRowGroupOpened = useCallback(
    (event: RowGroupOpenedEvent) => {
      if (!blotterID || !persistGridState || !persistRowGroupExpansion || !event.node.group || !event.node.id) {
        return;
      }

      const currentRowGroupExpansion = getState(blotterID)?.extraGridState?.rowGroupExpansion;

      // If there is no state persisted, we infer from the directionality of this individual expansion event
      const expansionState: RowGroupExpansionState['expansionState'] =
        currentRowGroupExpansion?.expansionState ?? (event.expanded ? 'all-collapsed' : 'all-expanded');

      const expansionExceptions = [...(currentRowGroupExpansion?.expansionExceptions ?? [])];
      const shouldBeException =
        (expansionState === 'all-expanded' && !event.expanded) ||
        (expansionState === 'all-collapsed' && event.expanded);

      // Based on the current state (all-expanded / all-collapsed), and the direction of this individual expansion event,
      // we determine if the ID should be added to or removed from the expansions list
      if (shouldBeException) {
        if (!expansionExceptions.includes(event.node.id)) {
          expansionExceptions.push(event.node.id);
        }
      } else {
        const index = expansionExceptions.indexOf(event.node.id);
        if (index >= 0) {
          expansionExceptions.splice(index, 1);
        }
      }

      persistRowGroupExpansionState({
        expansionState,
        expansionExceptions: trimExpansionExceptions({ expansionExceptions }),
      });
    },
    [blotterID, getState, persistGridState, persistRowGroupExpansion, persistRowGroupExpansionState]
  );

  const onColumnRowGroupChanged = useCallback(
    (event: ColumnRowGroupChangedEvent) => {
      const api = safeGridApi(event.api);
      if (!api) {
        return;
      }

      const someGroupNode = getFirstRowGroupNode(api);
      if (!someGroupNode) {
        persistRowGroupExpansionState(undefined);
        return;
      }

      persistRowGroupExpansionState({
        // If any row group is expanded after a column row group change, we are in all-expanded mode, and vice versa
        expansionState: someGroupNode.expanded ? 'all-expanded' : 'all-collapsed',
        expansionExceptions: [], // rowIDs of group nodes are recalculated on row group column change, so this is reset
      });
    },
    [persistRowGroupExpansionState]
  );

  const onStateUpdatedInterceptor = useCallback(
    (event: StateUpdatedEvent) => {
      if (!blotterID || !persistGridState) {
        return event;
      }

      // StateUpdatedEvents carry with them rowGroupExpansion details, but we are tracking that by ourselves in ExtraGridState
      // via the event handlers defined above. Our interceptor here just removes all rowGroupExpansion state from the GridState object
      if (event.state.rowGroupExpansion != null) {
        return {
          ...event,
          state: {
            ...event.state,
            rowGroupExpansion: undefined,
          },
        };
      }

      return event;
    },
    [blotterID, persistGridState]
  );

  return useMemo(
    () => ({
      eventHandlers: {
        onExpandOrCollapseAll,
        onRowGroupOpened,
        onColumnRowGroupChanged,
      },
      onStateUpdatedInterceptor,
    }),
    [onColumnRowGroupChanged, onExpandOrCollapseAll, onRowGroupOpened, onStateUpdatedInterceptor]
  );
}

/** Enforce a max-size exception list by dropping oldest entries first. */
function trimExpansionExceptions({ expansionExceptions }: { expansionExceptions: string[] }): string[] {
  const normalizedMax = Math.max(0, ROW_GROUP_EXPANSION_EXCEPTIONS_MAX);
  if (expansionExceptions.length <= normalizedMax) {
    return expansionExceptions;
  }

  const startIndex = Math.max(0, expansionExceptions.length - normalizedMax);
  return expansionExceptions.slice(startIndex);
}
